/* app.js — the page for /subtitle-converter.
 *
 * Everything that touches the DOM lives here; everything that touches subtitle
 * data lives in the sibling modules and is tested in Node without a browser.
 *
 * ── The one decision that shapes this file ─────────────────────────────────
 * There is no "Convert" button. Conversion of a two-hour film takes single-digit
 * milliseconds — it is string work — so the old flow (upload → pick format →
 * press Convert → look → press Download) spent three of its five steps waiting
 * for the user to authorise something instant. Here the result is recomputed on
 * every change and shown live, and the only button is the one that hands over
 * the file. Changing the format, the encoding or the frame rate re-renders in
 * front of you, which is also what makes the frame-rate control usable: you can
 * SEE the last cue move to the right place.
 */

import {
    load, loadBytes, reload, convert, outputName, expandZip,
    FORMATS, format, writableFormats, DESTINATIONS,
    ENCODING_CHOICES, writableEncodings, FPS_PRESETS, formatDuration, acceptAttribute
} from "./index.js";

const SLUG = "subtitle-converter";
const MAX_FILES = 50;

/* Strings come from the page (data-i18n JSON) so this file carries no English. */
const T = (() => {
    try { return JSON.parse(document.getElementById("scStrings").textContent); }
    catch (_) { return {}; }
})();
const PLURAL = (() => {
    try { return new Intl.PluralRules(window.__LOCALE || "en"); } catch (_) { return null; }
})();

/**
 * Look up a string.
 * @param {string} k
 * @param {object} [vars]  substituted as :name. `vars.count` (a raw number)
 *        selects the plural branch when the entry is an object; `vars.n` is
 *        what gets printed, so it can be locale-formatted separately.
 */
const t = (k, vars) => {
    let s = T[k];
    if (s && typeof s === "object" && !Array.isArray(s)) {
        const n = vars && (vars.count != null ? vars.count : vars.n);
        const cat = (PLURAL && n != null) ? PLURAL.select(Number(n) || 0) : "other";
        s = s[cat] != null ? s[cat] : (s.other != null ? s.other : s[Object.keys(s)[0]]);
    }
    if (s == null) s = k;
    if (vars) for (const key of Object.keys(vars)) s = String(s).split(":" + key).join(vars[key]);
    return String(s);
};

const $ = (id) => document.getElementById(id);
const el = {
    root: $("scApp"),
    zone: $("scUpload"),
    empty: $("scEmpty"),
    work: $("scWork"),
    fileBar: $("scFileBar"),
    fileList: $("scFileList"),
    fileMeta: $("scFileMeta"),
    addMore: $("scAddMore"),
    clear: $("scClear"),
    formatGrid: $("scFormats"),
    formatHint: $("scFormatHint"),
    destRow: $("scDest"),
    destRow2: $("scDest2"),
    destPick: $("scDestPick"),
    encSummary: $("scEncSummary"),
    encIn: $("scEncIn"),
    encOut: $("scEncOut"),
    encBlock: $("scEncBlock"),
    fpsBlock: $("scFpsBlock"),
    fpsSummary: $("scFpsSummary"),
    fpsIn: $("scFpsIn"),
    fpsOut: $("scFpsOut"),
    fpsCheck: $("scFpsCheck"),
    cleanSummary: $("scCleanSummary"),
    cleanBody: $("scCleanBody"),
    tabs: $("scTabs"),
    preview: $("scPreview"),
    cueTable: $("scCueTable"),
    source: $("scSource"),
    report: $("scReport"),
    mojibake: $("scMojibake"),
    mojiPreview: $("scMojiPreview"),
    mojiFix: $("scMojiFix"),
    mojiDismiss: $("scMojiDismiss"),
    download: $("scDownload"),
    downloadLabel: $("scDownloadLabel"),
    downloadSize: $("scDownloadSize"),
    busy: $("scBusy")
};

/* ── state ───────────────────────────────────────────────────────────────── */

const state = {
    docs: [],            /* one per picked file */
    active: 0,
    target: "srt",
    outEncoding: "utf-8",
    outFps: null,        /* null = follow the source */
    tab: "result",
    clean: {
        dropEmpty: true, sort: true, fixNegative: true, fixOverlap: true,
        mergeDuplicates: true, trimWhitespace: true, stripInvisible: true,
        removeAds: false, stripStyling: false
    },
    autoFixTiming: false,
    results: new Map(),  /* doc index → convert() output */
    mojiDismissed: false
};

/* ── telemetry ───────────────────────────────────────────────────────────── */

const ae = (name, data) => { try { window.__ae && window.__ae(name, data || {}); } catch (_) {} };
const funnel = (stage, data) => { try { window.__aeFunnel && window.__aeFunnel(stage, data || {}); } catch (_) {} };
const stage = (name) => { try { window.__stage && window.__stage(name); } catch (_) {} };

/* ── build the controls ──────────────────────────────────────────────────── */

/* The blade writes an accept= list and a formats= caption of its own, because
 * they have to exist before this module runs. They are a FLOOR, not the truth:
 * the registry is, and a format added there would otherwise be offered on the
 * chip grid while the file picker still turned it away. So both are rewritten
 * from the registry on boot — one source, two renderings. */
function syncPickerToRegistry() {
    const input = document.querySelector('[data-uploader-for="scUpload"]');
    if (input) input.setAttribute("accept", acceptAttribute());
    const caption = el.zone.querySelector(".tu-formats");
    if (caption) {
        caption.textContent = FORMATS.filter(f => f.read).map(f => f.label).join(" · ") + " · ZIP";
    }
}

function buildFormatGrid() {
    el.formatGrid.innerHTML = "";
    for (const f of writableFormats()) {
        const b = document.createElement("button");
        b.type = "button";
        b.className = "sc-fmt";
        b.dataset.fmt = f.id;
        b.setAttribute("role", "radio");
        b.setAttribute("aria-checked", "false");
        b.innerHTML = '<span class="sc-fmt-label">' + f.label + "</span>" +
            '<span class="sc-fmt-ext">.' + f.ext + "</span>";
        b.title = f.name;
        el.formatGrid.appendChild(b);
    }
}

function buildDestinations() {
    for (const host of [el.destRow, el.destRow2]) {
        if (!host) continue;
        host.innerHTML = "";
        for (const d of DESTINATIONS) {
            const b = document.createElement("button");
            b.type = "button";
            b.className = "sc-dest";
            b.dataset.to = d.to;
            b.dataset.dest = d.id;
            b.innerHTML = '<span class="sc-dest-icon" aria-hidden="true">' + d.icon + "</span>" +
                "<span>" + t("dest_" + d.id) + "</span>" +
                '<span class="sc-dest-fmt">' + (format(d.to) || {}).label + "</span>";
            host.appendChild(b);
        }
    }
}

function buildSelect(node, values, labeller) {
    node.innerHTML = "";
    for (const v of values) {
        const o = document.createElement("option");
        o.value = String(v);
        o.textContent = labeller ? labeller(v) : String(v);
        node.appendChild(o);
    }
}

function encodingLabel(enc) {
    if (enc === "utf-8") return "UTF-8";
    if (enc === "utf-8-bom") return "UTF-8 + BOM";
    return enc.toUpperCase();
}

/* ── file intake ─────────────────────────────────────────────────────────── */

let fflatePromise = null;
function loadFflate() {
    if (window.fflate) return Promise.resolve(window.fflate);
    if (!fflatePromise) {
        fflatePromise = new Promise((resolve, reject) => {
            const s = document.createElement("script");
            /* Versioned through the house primitive: /js is served immutable for
             * six months on one of the two shards, so a bare path would freeze
             * this file in returning visitors' caches. */
            s.src = (typeof window.__jsSrc === "function")
                ? window.__jsSrc("/js/vendor/fflate.min.js")
                : "/js/vendor/fflate.min.js";
            s.onload = () => resolve(window.fflate);
            s.onerror = () => reject(new Error("fflate"));
            document.head.appendChild(s);
        });
    }
    return fflatePromise;
}

async function intake(files) {
    const list = Array.from(files || []).filter(Boolean);
    if (!list.length) return;
    busy(true);
    try {
        const added = [];
        for (const file of list) {
            if (state.docs.length + added.length >= MAX_FILES) {
                window.__toast(t("too_many", { max: MAX_FILES }), "warning");
                break;
            }
            if (/\.zip$/i.test(file.name)) {
                const entries = await unzip(file);
                for (const e of entries) {
                    if (state.docs.length + added.length >= MAX_FILES) break;
                    added.push(loadBytes(e.bytes, e.name, {}));
                }
                ae("feature_use", { slug: SLUG, f: "zip_expanded", n: entries.length });
                continue;
            }
            added.push(await load(file));
        }
        if (!added.length) return;

        for (const doc of added) doc.limitKey = fileKeyFor(doc);
        const usable = added.filter(d => !d.error);
        const failed = added.filter(d => d.error);

        for (const d of failed) {
            window.__toast(t(d.error === "no_timings" ? "err_no_timings"
                : d.error === "no_entries" ? "err_no_entries" : "err_unknown", { name: d.name }), "error");
            ae("tool_error", { slug: SLUG, type: d.error });
        }
        if (!usable.length) return;

        const first = !state.docs.length;
        state.docs = state.docs.concat(usable);
        if (first) {
            state.active = 0;
            state.mojiDismissed = false;
            /* The first file decides the default target: converting a file to
             * its own format is the one choice nobody wants, so land on the
             * neighbour people ask for most from that source. */
            if (!userPickedFormat) state.target = defaultTargetFor(usable[0]);
            stage("loaded");
            funnel("file_loaded", {
                fmt: usable[0].format,
                enc: usable[0].encoding,
                cues: usable[0].cues.length,
                conf: Math.round((usable[0].encodingConfidence || 0) * 100)
            });
        }
        show();
        render();
    } catch (err) {
        window.__handleError(err, SLUG);
    } finally {
        busy(false);
    }
}

async function unzip(file) {
    const fflate = await loadFflate();
    const bytes = new Uint8Array(await file.arrayBuffer());
    const entries = expandZip(bytes, fflate);
    if (!entries.length) window.__toast(t("err_empty_zip"), "warning");
    return entries;
}

/* SRT is what most people are converting TO, and VTT is what SRT is most often
 * converted to — but a file that is ALREADY SRT wants the other answer. */
function defaultTargetFor(doc) {
    return doc.format === "srt" ? "vtt" : "srt";
}

/** The cap counts FILES, and the key has to survive the file being re-decoded
 *  in place (a change of encoding rebuilds the document object). Stamping it
 *  once at intake is what keeps "the same file again today is free" true. */
function fileKeyFor(doc) {
    try {
        if (window.__toolLimit && window.__toolLimit.fileKey) {
            return window.__toolLimit.fileKey({ name: doc.name, size: doc.size, lastModified: 0 });
        }
    } catch (_) {}
    return doc.name + "|" + doc.size;
}

/* ── rendering ───────────────────────────────────────────────────────────── */

let userPickedFormat = false;
let renderTimer = null;

function scheduleRender() {
    clearTimeout(renderTimer);
    renderTimer = setTimeout(render, 40);
}

function activeDoc() {
    return state.docs[state.active] || null;
}

function render() {
    const doc = activeDoc();
    if (!doc) return;

    /* 1. Recompute every document, because the download hands over all of them
     *    and the report has to describe the batch, not just what is on screen.
     *    A 50-file season is a few hundred milliseconds of string work. */
    state.results.clear();
    for (let i = 0; i < state.docs.length; i++) {
        try {
            state.results.set(i, convert(state.docs[i], convertOptions(state.docs[i])));
        } catch (err) {
            state.results.set(i, { error: err });
        }
    }

    renderFileBar();
    renderFormats();
    renderEncoding(doc);
    renderFps(doc);
    renderClean();
    renderPreview(doc);
    renderReport(doc);
    renderDownload();
    renderMojibake(doc);
}

/** The frame rate the OUTPUT is written at. */
function outputFps(doc) {
    if (state.outFps) return state.outFps;
    if ((format(doc.format) || {}).frameBased) return doc.fps;
    /* 23.976 rather than 25: it is what most releases a person is matching
     * subtitles to actually run at, and the control is on screen next to a
     * printed "the last cue lands at …" so a wrong guess is visible. */
    return 23.976;
}

function convertOptions(doc) {
    return {
        to: state.target,
        fps: outputFps(doc),
        encoding: state.outEncoding,
        repair: state.clean,
        autoFixTiming: state.autoFixTiming,
        paragraphs: true
    };
}

function renderFileBar() {
    const many = state.docs.length > 1;
    el.fileList.hidden = !many;
    el.fileBar.classList.toggle("sc-multi", many);

    const doc = activeDoc();
    const f = format(doc.format);
    el.fileMeta.innerHTML = "";
    const bits = [
        { cls: "sc-tag", text: f ? f.label : "?" },
        { cls: "", text: t("n_cues", { count: doc.cues.length, n: doc.cues.length.toLocaleString(window.__LOCALE || "en") }) },
        { cls: "", text: formatDuration(doc.duration) }
    ];
    if (doc.skipped) bits.push({ cls: "sc-warn", text: t("n_skipped", { count: doc.skipped, n: doc.skipped }) });
    for (const b of bits) {
        const s = document.createElement("span");
        s.className = b.cls;
        s.textContent = b.text;
        el.fileMeta.appendChild(s);
    }
    document.getElementById("scFileName").textContent = doc.name;

    if (many) {
        el.fileList.innerHTML = "";
        state.docs.forEach((d, i) => {
            const b = document.createElement("button");
            b.type = "button";
            b.className = "sc-file-chip" + (i === state.active ? " active" : "");
            b.dataset.idx = String(i);
            b.textContent = d.name;
            el.fileList.appendChild(b);
        });
    }
}

function renderFormats() {
    for (const b of el.formatGrid.children) {
        const on = b.dataset.fmt === state.target;
        b.classList.toggle("active", on);
        b.setAttribute("aria-checked", on ? "true" : "false");
        b.tabIndex = on ? 0 : -1;
    }
    for (const host of [el.destRow, el.destRow2]) {
        for (const b of host.children) b.classList.toggle("active", b.dataset.to === state.target);
    }

    const f = format(state.target);
    const res = state.results.get(state.active);
    const parts = [f.name];
    const use = t("fmt_use_" + state.target);
    if (use && use !== "fmt_use_" + state.target) parts.push(use);
    if (res && res.lost && res.lost.length) {
        parts.push(t("will_lose", { what: res.lost.map(l => t("lose_" + l)).join(", ") }));
    }
    el.formatHint.textContent = parts.join(" · ");
    el.formatHint.classList.toggle("sc-warn", !!(res && res.lost && res.lost.length));
}

function renderEncoding(doc) {
    if (el.encIn.value !== doc.encoding) el.encIn.value = doc.encoding;
    el.encOut.value = state.outEncoding;
    const low = !doc.encodingForced && doc.encodingConfidence < 0.5 && doc.encoding !== "utf-8";
    el.encSummary.innerHTML = "";
    const a = document.createElement("strong");
    a.textContent = encodingLabel(doc.encoding);
    el.encSummary.appendChild(a);
    el.encSummary.appendChild(document.createTextNode(" → " + encodingLabel(state.outEncoding)));
    if (low) {
        const q = document.createElement("span");
        q.className = "sc-warn";
        q.textContent = " " + t("enc_unsure");
        el.encSummary.appendChild(q);
    }
    el.encBlock.classList.toggle("sc-attention", low);
    /* Same rule as the frame rate: fold the control away when the answer is
     * certain, put it in front of the user when it is a coin toss. A sniffer
     * that is wrong quietly is worse than none, and the override is the whole
     * remedy — so on a low-confidence read it opens itself, once. */
    if (low && !el.encBlock.dataset.nudged) {
        el.encBlock.open = true;
        el.encBlock.dataset.nudged = "1";
    }
}

function renderFps(doc) {
    const srcFrames = !!(format(doc.format) || {}).frameBased;
    const dstFrames = !!(format(state.target) || {}).frameBased;
    const need = srcFrames || dstFrames;
    el.fpsBlock.hidden = !need;
    if (!need) return;

    el.fpsIn.value = String(nearestFps(doc.fps));
    el.fpsIn.disabled = !srcFrames;
    el.fpsIn.closest(".sc-field").hidden = !srcFrames;
    el.fpsOut.value = String(nearestFps(outputFps(doc)));
    el.fpsOut.closest(".sc-field").hidden = !dstFrames;

    const summary = [];
    if (srcFrames) summary.push(t("fps_read", { fps: nearestFps(doc.fps) }));
    if (dstFrames) summary.push(t("fps_write", { fps: nearestFps(outputFps(doc)) }));
    el.fpsSummary.textContent = summary.join(" → ");

    /* The sanity check that makes the control usable: the last cue's timecode.
     * Compare it with the length of the video and a wrong frame rate is obvious
     * in one glance — no other feedback in this tool is that direct. */
    el.fpsCheck.textContent = t("fps_check", {
        time: formatDuration(doc.duration),
        n: doc.cues.length
    });
    const guessing = srcFrames && !doc.fpsFromFile;
    el.fpsBlock.classList.toggle("sc-attention", guessing);
    /* Open it when the rate was GUESSED. A frame-based file that declares its
     * own rate needs no attention; one that does not is the case where a wrong
     * default drifts the whole film, and a control folded behind a summary is a
     * control the person who needed it never opened. Only forced open once, so
     * a user who closes it is not fought with on the next keystroke. */
    if (guessing && !el.fpsBlock.dataset.nudged) {
        el.fpsBlock.open = true;
        el.fpsBlock.dataset.nudged = "1";
    }
}

function nearestFps(v) {
    let best = FPS_PRESETS[0].value, d = Infinity;
    for (const p of FPS_PRESETS) {
        const diff = Math.abs(p.value - v);
        if (diff < d) { d = diff; best = p.value; }
    }
    return best;
}

function renderClean() {
    let fixed = 0, ads = 0;
    for (const [, r] of state.results) {
        if (!r || r.error) continue;
        fixed += r.fixCount || 0;
        ads += (r.removedLines || []).length;
    }
    el.cleanSummary.textContent = fixed
        ? t("clean_fixed", { n: fixed })
        : t("clean_none");
    el.cleanSummary.classList.toggle("sc-ok", fixed > 0);
    if (ads) el.cleanSummary.textContent += " · " + t("clean_ads", { n: ads });
}

function renderPreview(doc) {
    const res = state.results.get(state.active);
    for (const b of el.tabs.children) b.classList.toggle("active", b.dataset.tab === state.tab);
    el.preview.hidden = state.tab !== "result";
    el.cueTable.hidden = state.tab !== "cues";
    el.source.hidden = state.tab !== "source";

    if (state.tab === "result") {
        if (!res || res.error) {
            el.preview.textContent = t("err_convert");
            return;
        }
        /* Only the head of the file: a two-hour ASS is 400 KB, and painting it
         * into a <pre> costs more than the conversion did. */
        const LIMIT = 8000;
        el.preview.textContent = res.text.length > LIMIT
            ? res.text.slice(0, LIMIT) + "\n…"
            : res.text;
    } else if (state.tab === "source") {
        el.source.textContent = doc.text.length > 8000 ? doc.text.slice(0, 8000) + "\n…" : doc.text;
    } else {
        renderCueTable(doc, res);
    }
}

function renderCueTable(doc, res) {
    const cues = (res && res.cues) || doc.cues;
    const issues = new Map();
    if (res && res.quality) for (const i of res.quality.issues) issues.set(i.index, i);

    /* 1 200 rows of DOM is slow to build and pointless to read. Show the first
     * 200, and if there are flagged cues show THOSE first — the list exists to
     * find problems, not to be a viewer. */
    const flagged = [];
    const rest = [];
    cues.forEach((c, i) => (issues.has(i) ? flagged : rest).push(i));
    const order = flagged.concat(rest).slice(0, 200);

    const rows = order.map(i => {
        const c = cues[i];
        const issue = issues.get(i);
        const marks = issue ? issue.faults.map(f => '<span class="sc-flag sc-flag-' + f + '">' + t("fault_" + f) + "</span>").join("") : "";
        return '<tr class="' + (issue ? "sc-row-bad" : "") + '">' +
            "<td>" + (i + 1) + "</td>" +
            '<td class="sc-tc">' + hhmmss(c.start) + "</td>" +
            '<td class="sc-tc">' + ((c.end - c.start) / 1000).toFixed(1) + "s</td>" +
            "<td>" + (issue ? issue.cps.toFixed(1) : cps(c).toFixed(1)) + "</td>" +
            '<td class="sc-cue-text">' + escapeHtml(String(c.text).replace(/<[^>]*>/g, "").replace(/\n/g, " ⏎ ")) + marks + "</td>" +
            "</tr>";
    }).join("");

    el.cueTable.innerHTML =
        "<table><thead><tr><th>#</th><th>" + t("col_start") + "</th><th>" + t("col_dur") +
        "</th><th>" + t("col_cps") + "</th><th>" + t("col_text") + "</th></tr></thead><tbody>" +
        rows + "</tbody></table>" +
        (cues.length > order.length ? '<p class="sc-more">' + t("showing_n", { n: order.length, total: cues.length }) + "</p>" : "");
}

function cps(c) {
    const chars = String(c.text).replace(/<[^>]*>/g, "").replace(/\n/g, "").length;
    return chars / Math.max(0.2, (c.end - c.start) / 1000);
}

function hhmmss(ms) {
    const s = Math.max(0, Math.round(ms / 1000));
    const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
    return (h ? h + ":" : "") + String(m).padStart(2, "0") + ":" + String(sec).padStart(2, "0");
}

function escapeHtml(s) {
    return String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function renderReport(doc) {
    const res = state.results.get(state.active);
    el.report.innerHTML = "";
    if (!res || res.error || !res.quality) { el.report.hidden = true; return; }
    const q = res.quality;
    const items = [];
    const order = ["fast", "longLine", "manyLines", "tooShort", "tooLong", "overlap", "tightGap"];
    for (const k of order) {
        if (q.counts[k]) items.push({ k, n: q.counts[k] });
    }
    if (!items.length) {
        el.report.hidden = false;
        el.report.innerHTML = '<span class="sc-ok">' + t("quality_clean", { limit: q.limits.cps }) + "</span>";
        return;
    }
    el.report.hidden = false;
    const strip = document.createElement("div");
    strip.className = "sc-report-items";
    for (const it of items) {
        const s = document.createElement("span");
        s.className = "sc-report-item";
        s.textContent = t("issue_" + it.k, { n: it.n, limit: q.limits.cps, chars: q.limits.chars });
        strip.appendChild(s);
    }
    el.report.appendChild(strip);

    const fixable = q.counts.fast || q.counts.tooShort || q.counts.tooLong || q.counts.overlap || q.counts.tightGap;
    if (fixable && !state.autoFixTiming) {
        const b = document.createElement("button");
        b.type = "button";
        b.className = "sc-fix-btn";
        b.textContent = t("fix_timing");
        b.addEventListener("click", () => {
            state.autoFixTiming = true;
            ae("feature_use", { slug: SLUG, f: "timing_autofix" });
            render();
        });
        el.report.appendChild(b);
    } else if (state.autoFixTiming) {
        const s = document.createElement("span");
        s.className = "sc-ok";
        s.textContent = t("timing_fixed");
        el.report.appendChild(s);
    }
}

function renderDownload() {
    const many = state.docs.length > 1;
    let bytes = 0;
    for (const [, r] of state.results) if (r && !r.error) bytes += r.bytes.length;
    const name = many
        ? t("download_batch", { count: state.docs.length, n: state.docs.length })
        : outputName(activeDoc().name, state.target);
    el.downloadLabel.textContent = name;
    el.downloadSize.textContent = humanBytes(bytes);
    el.download.disabled = !state.results.size;
}

function humanBytes(b) {
    if (b < 1024) return b + " B";
    if (b < 1048576) return (b / 1024).toFixed(1) + " KB";
    return (b / 1048576).toFixed(1) + " MB";
}

function renderMojibake(doc) {
    const show = !state.mojiDismissed && doc.mojibake && doc.mojibake.found;
    el.mojibake.hidden = !show;
    if (show) {
        el.mojiPreview.textContent = doc.mojibake.preview;
        ae("feature_use", { slug: SLUG, f: "mojibake_detected" });
    }
}

/* ── delivery ────────────────────────────────────────────────────────────── */

async function download() {
    if (!state.docs.length) return;

    /* The cap counts FILES, not presses: a season of twelve costs twelve, and
     * re-exporting the same file in another format later today costs nothing.
     * The keys were stamped at intake so a re-decode does not mint new ones. */
    const keys = state.docs.map(d => d.limitKey);
    const run = window.__toolLimit ? window.__toolLimit.begin(SLUG, keys) : { commit() {}, abort() {} };
    if (!run) return;

    busy(true);
    try {
        const items = [];
        const used = new Set();
        for (let i = 0; i < state.docs.length; i++) {
            const r = state.results.get(i);
            if (!r || r.error) continue;
            const name = outputName(state.docs[i].name, state.target, used);
            used.add(name);
            items.push({ name, blob: new Blob([r.bytes], { type: (format(state.target) || {}).mime || "text/plain" }) });
        }
        if (!items.length) { run.abort(); return; }

        if (items.length === 1) {
            const ok = await window.__downloadFile(items[0].blob, items[0].name);
            if (ok === false) { run.abort(); return; }
        } else {
            const out = await window.__batchDeliver.deliver(items, {
                zipName: t("zip_name", { fmt: (format(state.target) || {}).label }) + ".zip",
                slug: SLUG
            });
            if (!out || !out.written) { run.abort(); return; }
        }
        run.commit();

        const res = state.results.get(state.active);
        try {
            window.__aeToolComplete && window.__aeToolComplete({
                slug: SLUG,
                files: items.length,
                from: activeDoc().format,
                to: state.target,
                enc_in: activeDoc().encoding,
                enc_out: state.outEncoding,
                cues: activeDoc().cues.length,
                fixes: res ? res.fixCount : 0,
                lost: res && res.lost.length ? res.lost.join(",") : "",
                autofix: state.autoFixTiming ? 1 : 0
            });
        } catch (_) {}
        window.__toast(t("saved"), "success");
    } catch (err) {
        run.abort();
        window.__handleError(err, SLUG);
    } finally {
        busy(false);
    }
}

/* ── plumbing ────────────────────────────────────────────────────────────── */

function busy(on) {
    el.busy.hidden = !on;
    el.root.classList.toggle("sc-busy", !!on);
}

function show() {
    swapView(() => {
        el.empty.hidden = state.docs.length > 0;
        el.work.hidden = state.docs.length === 0;
    });
}

/* View transitions make the empty screen and the workspace read as one object
 * changing rather than two panels blinking.
 *
 * ⚠️ The DOM update MUST still happen exactly once and in order. startViewTransition
 * defers its callback to the next frame, so two calls close together — clearing
 * the file and immediately picking another, which is what a person does and what
 * a test does — can interleave: the second transition skips the first and its
 * callback lands after, leaving the workspace hidden with a file loaded. So a
 * transition already in flight means the next swap applies straight away. */
let viewTransition = null;
function swapView(fn) {
    const canAnimate = typeof document.startViewTransition === "function" &&
        !matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (!canAnimate || viewTransition || !window.__viewTransition) { fn(); return; }
    viewTransition = window.__viewTransition(fn);
    if (!viewTransition) return;   // выполнено синхронно, анимации не было
    viewTransition.finished.catch(function () {}).then(function () { viewTransition = null; });
}

function reset() {
    state.docs = [];
    state.active = 0;
    state.results.clear();
    state.autoFixTiming = false;
    state.mojiDismissed = false;
    /* Fold the panels back. A <details> keeps its open state across a file
     * change, so a session that opened three of them to look at something once
     * kept a taller workspace for every file afterwards — the settings survive
     * in `state`, only the disclosure resets. */
    for (const d of el.root.querySelectorAll(".sc-set, .sc-dest-pick")) d.open = false;
    if (el.fpsBlock) delete el.fpsBlock.dataset.nudged;
    if (el.encBlock) delete el.encBlock.dataset.nudged;
    swapView(() => { el.empty.hidden = false; el.work.hidden = true; });
}

function wire() {
    /* ⚠️ NOT a raw addEventListener. This module is loaded as type=module, so
     * it executes after the document is parsed — and on a slow connection the
     * user can have picked a file BEFORE that. The uploader has already fired
     * tool-uploader:file by then, the event reaches nobody, and the tool sits
     * dead until the file is picked a second time. __bindUploaderFile attaches
     * the listener AND replays a pick that already happened (public/js/
     * tool-uploader.js; the class was found on /vocal-remover in 2026-05). */
    const onPicked = (file, detail) => {
        const d = detail || {};
        intake(d.files && d.files.length ? d.files : [file]);
    };
    if (typeof window.__bindUploaderFile === "function") {
        window.__bindUploaderFile(el.zone, onPicked);
    } else {
        el.zone.addEventListener("tool-uploader:file", (e) => {
            const d = e.detail || {};
            onPicked(d.file, d);
        });
    }
    el.zone.addEventListener("tool-uploader:clear", reset);
    el.clear.addEventListener("click", reset);
    el.addMore.addEventListener("click", () => {
        const input = document.querySelector('[data-uploader-for="scUpload"]');
        if (input) input.click();
    });

    el.formatGrid.addEventListener("click", (e) => {
        const b = e.target.closest(".sc-fmt");
        if (!b) return;
        userPickedFormat = true;
        state.target = b.dataset.fmt;
        ae("feature_use", { slug: SLUG, f: "target_pick", to: state.target });
        render();
    });
    /* Arrow keys inside the grid, because it is a radio group and a keyboard
     * user should not have to tab through sixteen buttons to reach JSON. */
    el.formatGrid.addEventListener("keydown", (e) => {
        if (!/^Arrow/.test(e.key)) return;
        const items = Array.from(el.formatGrid.children);
        const i = items.findIndex(b => b.dataset.fmt === state.target);
        const cols = Math.max(1, Math.round(el.formatGrid.clientWidth / (items[0].offsetWidth || 80)));
        const step = e.key === "ArrowRight" ? 1 : e.key === "ArrowLeft" ? -1 : e.key === "ArrowDown" ? cols : -cols;
        const next = Math.min(items.length - 1, Math.max(0, i + step));
        if (next === i) return;
        e.preventDefault();
        userPickedFormat = true;
        state.target = items[next].dataset.fmt;
        render();
        items[next].focus();
    });

    const onDestination = (e) => {
        const b = e.target.closest(".sc-dest");
        if (!b) return;
        userPickedFormat = true;
        state.target = b.dataset.to;
        ae("feature_use", { slug: SLUG, f: "destination_pick", dest: b.dataset.dest });
        /* Close the picker once it has answered its question — leaving it open
         * would push the format grid down for the rest of the session. */
        if (el.destPick) el.destPick.open = false;
        if (state.docs.length) render();
        else for (const host of [el.destRow, el.destRow2]) {
            for (const other of host.children) other.classList.toggle("active", other.dataset.to === state.target);
        }
    };
    el.destRow.addEventListener("click", onDestination);
    el.destRow2.addEventListener("click", onDestination);
    /* The grid lives outside the <details> so it can span the full band width;
     * the summary only toggles it. */
    if (el.destPick) el.destPick.addEventListener("toggle", () => { el.destRow2.hidden = !el.destPick.open; });

    el.fileList.addEventListener("click", (e) => {
        const b = e.target.closest(".sc-file-chip");
        if (!b) return;
        state.active = +b.dataset.idx;
        render();
    });

    el.tabs.addEventListener("click", (e) => {
        const b = e.target.closest("[data-tab]");
        if (!b) return;
        state.tab = b.dataset.tab;
        renderPreview(activeDoc());
    });

    el.encIn.addEventListener("change", () => {
        const doc = activeDoc();
        if (!doc) return;
        const next = reload(doc, { encoding: el.encIn.value, repairMojibake: false });
        next.limitKey = doc.limitKey;
        state.docs[state.active] = next;
        ae("feature_use", { slug: SLUG, f: "encoding_override", to: el.encIn.value });
        render();
    });
    el.encOut.addEventListener("change", () => {
        state.outEncoding = el.encOut.value;
        render();
    });
    el.fpsIn.addEventListener("change", () => {
        const doc = activeDoc();
        if (!doc) return;
        const next = reload(doc, { fps: parseFloat(el.fpsIn.value) });
        next.limitKey = doc.limitKey;
        state.docs[state.active] = next;
        ae("feature_use", { slug: SLUG, f: "fps_override", fps: el.fpsIn.value });
        render();
    });
    el.fpsOut.addEventListener("change", () => {
        state.outFps = parseFloat(el.fpsOut.value);
        render();
    });

    el.cleanBody.addEventListener("change", (e) => {
        const box = e.target.closest("input[type=checkbox][data-clean]");
        if (!box) return;
        state.clean[box.dataset.clean] = box.checked;
        if (box.dataset.clean === "removeAds" && box.checked) ae("feature_use", { slug: SLUG, f: "remove_ads" });
        scheduleRender();
    });

    el.mojiFix.addEventListener("click", () => {
        const doc = activeDoc();
        const next = reload(doc, { repairMojibake: true });
        next.limitKey = doc.limitKey;
        state.docs[state.active] = next;
        state.mojiDismissed = true;
        ae("feature_use", { slug: SLUG, f: "mojibake_repaired" });
        window.__toast(t("moji_done"), "success");
        render();
    });
    el.mojiDismiss.addEventListener("click", () => {
        state.mojiDismissed = true;
        el.mojibake.hidden = true;
    });

    el.download.addEventListener("click", download);
}

/* ── boot ────────────────────────────────────────────────────────────────── */

function boot() {
    if (!el.root) return;
    syncPickerToRegistry();
    buildFormatGrid();
    buildDestinations();
    buildSelect(el.encIn, ENCODING_CHOICES, encodingLabel);
    buildSelect(el.encOut, writableEncodings(), encodingLabel);
    buildSelect(el.fpsIn, FPS_PRESETS.map(p => p.value));
    buildSelect(el.fpsOut, FPS_PRESETS.map(p => p.value));
    wire();
    stage("ready");
    /* Exposed for the end-to-end tests, which drive the real page rather than a
     * copy of it — a harness that re-implements the tool proves nothing. */
    window.__subtitleConverter = { state, render, intake, loadBytes, convert };
}

if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", boot);
else boot();
